import React, { useEffect, useState, useCallback } from 'react';
import "./index.scss";
import { Row, Col, Card, Button, Modal, Form, Badge } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { useApiServices } from '../../services/api-endpoints/useApiServices';
import { useBreadcrumb } from '../../services/breadcrumb-services/BreadcrumbContext';
import { DateTimeFormat, IsEmptyGuid } from '../../helper/Formatters';
import ImageViewer from '../../components/image-viewer/ImageViewer';
import { toast } from 'react-toastify';
import CommentsBox from '../../components/comments-box';
import AccountsBox from '../../components/accounts-box';
import ImageUpload from '../../components/image-upload';
import TransactionStatusSelect from '../../components/transaction-status-select';
import { useChat } from '../../services/chat/ChatContext';
import { useSignalR } from '../../services/signalR/SignalRProvider';

// ─── Map helper ────────────────────────────────────────────────────────────────
const MapEmbed = ({ lat, lng, label }) => {
    if (!lat || !lng) return null;
    const delta = 0.008;
    const bbox = `${lng - delta},${lat - delta},${lng + delta},${lat + delta}`;
    const src = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat},${lng}`;
    const gmapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;
    return (
        <div style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
            <iframe
                title={label || 'map'}
                src={src}
                width="100%"
                height="200"
                style={{ border: 'none', display: 'block' }}
                loading="lazy"
            />
            <a
                href={gmapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: 6, padding: '8px 12px', background: '#f9fafb',
                    color: '#1e3a5f', fontSize: 13, fontWeight: 600,
                    textDecoration: 'none', borderTop: '1px solid #e5e7eb',
                }}
            >
                <i className="fas fa-external-link-alt" style={{ fontSize: 11 }} />
                เปิดใน Google Maps
            </a>
        </div>
    );
};

// ─── Info Row ───────────────────────────────────────────────────────────────
const InfoRow = ({ label, value, children }) => (
    <div style={{ display: 'flex', gap: 8, marginBottom: 10, fontSize: 14 }}>
        <span style={{ color: '#6b7280', minWidth: 110, flexShrink: 0 }}>{label}</span>
        <span style={{ color: '#1f2937', fontWeight: 500 }}>{children || value || '-'}</span>
    </div>
);

// ─── Summary Row ────────────────────────────────────────────────────────────
const SummaryRow = ({ label, value, bold, color }) => (
    <div style={{
        display: 'flex', justifyContent: 'space-between',
        padding: '6px 0', fontSize: bold ? 15 : 14,
        fontWeight: bold ? 700 : 400,
        color: color || '#1f2937',
        borderTop: bold ? '1px solid #e5e7eb' : 'none',
        marginTop: bold ? 4 : 0,
    }}>
        <span style={{ color: bold ? (color || '#1f2937') : '#6b7280' }}>{label}</span>
        <span>{value}</span>
    </div>
);

// ─── Main Component ──────────────────────────────────────────────────────────
const TransactionsDetails = () => {
    const { id } = useParams();
    const api = useApiServices();
    const { setCustomName } = useBreadcrumb();
    const { messages } = useSignalR();
    const { setChatUser } = useChat();

    const [data, setData] = useState({});
    const [logs, setLogs] = useState([]);
    const [files, setFiles] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [file, setFile] = useState(null);
    const [nextStatus, setNextStatus] = useState(null);
    const [paymentDetails, setPaymentDetails] = useState({ description: '' });

    const handleClose = () => { setShowModal(false); setFile(null); };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const { isSuccess } = await api.ConfirmTransactionAsync(id, { fileUploadId: file.id });
        if (isSuccess) {
            toast.success('ยืนยันการชำระเงินแล้ว');
            fetchTransactionDetails(id);
        }
        handleClose();
    };

    const fetchTransactionDetails = useCallback(async (txId) => {
        if (!txId) return;
        setNextStatus(null);
        const { isSuccess, result } = await api.GetTransactionDetailsAsync(txId);
        if (isSuccess) {
            setData(result);
            setCustomName(`#${result.transactionNumber}`);
            const [logsRes, filesRes] = await Promise.all([
                api.GetTransactionLogsAsync(txId),
                api.GetTransactionFilesAsync(txId),
            ]);
            if (logsRes.isSuccess) setLogs(logsRes.result);
            if (filesRes.isSuccess) setFiles(filesRes.result);
        }
    }, [api, setCustomName]);

    useEffect(() => {
        if (id) fetchTransactionDetails(id);
    }, [id]);

    useEffect(() => {
        if (!messages?.length) return;
        const last = messages[messages.length - 1];
        if (last && (last.type === 'Transaction' || last.type === 'Payment')) {
            if (last.keys?.[0] === id) fetchTransactionDetails(id);
        }
    }, [messages]);

    const totalSale = data.transactionToProducts?.reduce((s, p) => s + (p.total || 0), 0) || 0;
    const totalCost = (data.transactionToProducts?.reduce((s, p) => s + (p.costTotal || 0), 0) || 0) + (data.shippingPrice || 0);
    const shipping = data.shippingPrice || 0;
    const serviceFee = data.serviceFee || 0;
    const discount = data.discount || 0;
    const grandTotal = totalSale + shipping + serviceFee - discount;
    const profit = grandTotal - totalCost;

    const addrLat = data.address?.latitude;
    const addrLng = data.address?.longitude;

    return (
        <React.Fragment>
            <Row className="g-3">
                {/* ── Left Column ──────────────────────────────────────── */}
                <Col md={4}>
                    {/* Customer Card */}
                    <Card style={cardStyle} className="mb-3">
                        <Card.Body>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                                <div style={avatarStyle}>
                                    {data.user?.firstName?.[0]}{data.user?.lastName?.[0]}
                                </div>
                                <div>
                                    <div style={{ fontWeight: 700, fontSize: 15, color: '#1f2937' }}>
                                        {data.user?.firstName} {data.user?.lastName}
                                    </div>
                                    <Badge pill style={{ background: '#e0e7ff', color: '#4f46e5', fontWeight: 500, fontSize: 11 }}>
                                        {data.user?.userType?.name || 'User'}
                                    </Badge>
                                </div>
                            </div>
                            <InfoRow label="อีเมล">{data.user?.email || '-'}</InfoRow>
                            <InfoRow label="เบอร์ติดต่อ">{data.user?.phone || '-'}</InfoRow>
                            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                                <Link to={`/users/${data.userId}`} className="btn btn-outline-primary btn-sm" style={{ flex: 1, textAlign: 'center' }}>
                                    <i className="fa fa-user" style={{ marginRight: 5 }} />โปรไฟล์
                                </Link>
                                <button
                                    className="btn btn-primary btn-sm"
                                    style={{ flex: 1 }}
                                    onClick={() => setChatUser(data.userId)}
                                >
                                    <i className="fa fa-comment" style={{ marginRight: 5 }} />แชท
                                </button>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* Store Account */}
                    {data.store?.entityUniqueIdentifierId && (
                        <Card style={cardStyle} className="mb-3">
                            <Card.Body>
                                <AccountsBox title={`บัญชีร้าน ${data.store?.name}`} euid={data.store?.entityUniqueIdentifierId} readOnly={true} />
                            </Card.Body>
                        </Card>
                    )}

                    {/* Customer Account */}
                    {data.user?.entityUniqueIdentifierId && (
                        <Card style={cardStyle} className="mb-3">
                            <Card.Body>
                                <AccountsBox title="บัญชีลูกค้า" euid={data.user?.entityUniqueIdentifierId} readOnly={true} />
                            </Card.Body>
                        </Card>
                    )}
                </Col>

                {/* ── Right Column ─────────────────────────────────────── */}
                <Col md={8}>
                    {/* Order Header */}
                    <Card style={cardStyle} className="mb-3">
                        <Card.Body style={{ padding: '16px 20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
                                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                                    <span style={{ fontSize: 18, fontWeight: 700, color: '#1e3a5f' }}>
                                        #{data.transactionNumber || '-'}
                                    </span>
                                    <TransactionStatusSelect
                                        onCanceled={() => setNextStatus(null)}
                                        forceChange={nextStatus}
                                        transationId={data.id}
                                        onSuccess={() => fetchTransactionDetails(id)}
                                        current={data.currentStatusKey}
                                    />
                                    {data.currentStatusKey === 0 && (
                                        <button
                                            onClick={() => setNextStatus(10)}
                                            style={{
                                                display: 'inline-flex', alignItems: 'center', gap: 7,
                                                padding: '6px 14px',
                                                background: 'linear-gradient(135deg, #1e3a5f, #2d6a9f)',
                                                border: 'none', borderRadius: 20,
                                                color: '#fff', fontSize: 13, fontWeight: 600,
                                                cursor: 'pointer',
                                                boxShadow: '0 2px 8px rgba(30,58,95,0.25)',
                                                transition: 'all 0.15s',
                                            }}
                                            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 14px rgba(30,58,95,0.4)'}
                                            onMouseLeave={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(30,58,95,0.25)'}
                                        >
                                            <span style={{
                                                width: 18, height: 18, borderRadius: '50%',
                                                background: 'rgba(255,255,255,0.2)',
                                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                flexShrink: 0,
                                            }}>
                                                <i className="fa fa-truck" style={{ fontSize: 9 }} />
                                            </span>
                                            ยืนยันค่าจัดส่ง
                                        </button>
                                    )}
                                    {data.currentStatusKey === 11 && (
                                        <button
                                            onClick={() => setShowModal(true)}
                                            style={{
                                                display: 'inline-flex', alignItems: 'center', gap: 7,
                                                padding: '6px 14px',
                                                background: 'linear-gradient(135deg, #16a34a, #22c55e)',
                                                border: 'none', borderRadius: 20,
                                                color: '#fff', fontSize: 13, fontWeight: 600,
                                                cursor: 'pointer',
                                                boxShadow: '0 2px 8px rgba(22,163,74,0.25)',
                                                transition: 'all 0.15s',
                                            }}
                                            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 14px rgba(22,163,74,0.4)'}
                                            onMouseLeave={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(22,163,74,0.25)'}
                                        >
                                            <span style={{
                                                width: 18, height: 18, borderRadius: '50%',
                                                background: 'rgba(255,255,255,0.2)',
                                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                flexShrink: 0,
                                            }}>
                                                <i className="fa fa-check" style={{ fontSize: 9 }} />
                                            </span>
                                            ยืนยัน Order
                                        </button>
                                    )}
                                </div>
                                <span style={{ fontSize: 12, color: '#9ca3af' }}>
                                    <i className="fa fa-clock" style={{ marginRight: 4 }} />
                                    {DateTimeFormat(data.createDate)}
                                </span>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* Order Info + Map */}
                    <Card style={cardStyle} className="mb-3">
                        <Card.Body>
                            <Row>
                                <Col md={addrLat ? 7 : 12}>
                                    <div style={sectionTitle}>ข้อมูลคำสั่ง</div>
                                    <InfoRow label="ร้านค้า">
                                        <Link to={`/stores/${data.storeId}`} style={{ color: '#1e3a5f' }}>
                                            {data.store?.name || '-'}
                                        </Link>
                                    </InfoRow>
                                    <InfoRow label="ลูกค้า">
                                        <Link to={`/users/${data.userId}`} style={{ color: '#1e3a5f' }}>
                                            {data.user?.firstName} {data.user?.lastName}
                                        </Link>
                                    </InfoRow>
                                    <InfoRow label="นัดหมาย">
                                        {data.appointmentDateTime ? DateTimeFormat(data.appointmentDateTime) : 'ทันที'}
                                    </InfoRow>

                                    <div style={{ ...sectionTitle, marginTop: 16 }}>ที่อยู่จัดส่ง</div>
                                    <InfoRow label="ผู้รับ">{data.address?.contact?.name}</InfoRow>
                                    <InfoRow label="เบอร์ติดต่อ">{data.address?.contact?.phone}</InfoRow>
                                    <InfoRow label="ที่อยู่">
                                        {[data.address?.addressLine1, data.address?.addressLine2, data.address?.postalCode]
                                            .filter(Boolean).join(' ')}
                                    </InfoRow>
                                    {addrLat && addrLng && (
                                        <InfoRow label="พิกัด">
                                            <a
                                                href={`https://www.google.com/maps?q=${addrLat},${addrLng}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ color: '#3b82f6', fontSize: 12 }}
                                            >
                                                {addrLat.toFixed(5)}, {addrLng.toFixed(5)}
                                                <i className="fas fa-external-link-alt" style={{ marginLeft: 5, fontSize: 10 }} />
                                            </a>
                                        </InfoRow>
                                    )}
                                </Col>
                                {addrLat && addrLng && (
                                    <Col md={5}>
                                        <div style={sectionTitle}>แผนที่</div>
                                        <MapEmbed lat={addrLat} lng={addrLng} label="ที่อยู่จัดส่ง" />
                                    </Col>
                                )}
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Products */}
                    <Card style={cardStyle} className="mb-3">
                        <Card.Body>
                            <div style={sectionTitle}>รายการสินค้า</div>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                                    <thead>
                                        <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                                            {['สินค้า', 'จำนวน', 'ราคาขาย', 'ราคาทุน', 'รวมขาย', 'รวมทุน'].map(h => (
                                                <th key={h} style={{ padding: '8px 12px', fontWeight: 600, color: '#6b7280', fontSize: 12, textAlign: h === 'สินค้า' ? 'left' : 'right' }}>
                                                    {h}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.transactionToProducts?.map((item) => (
                                            <tr key={item.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                                <td style={{ padding: '10px 12px' }}>
                                                    <div style={{ fontWeight: 600, color: '#1f2937' }}>{item.product?.nameTh}</div>
                                                    <div style={{ fontSize: 11, color: '#9ca3af' }}>{item.product?.sku}</div>
                                                </td>
                                                <td style={{ padding: '10px 12px', textAlign: 'right' }}>{item.quantity}</td>
                                                <td style={{ padding: '10px 12px', textAlign: 'right' }}>{item.price?.toLocaleString()}</td>
                                                <td style={{ padding: '10px 12px', textAlign: 'right', color: '#6b7280' }}>{item.costPrice?.toLocaleString()}</td>
                                                <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>{item.total?.toLocaleString()}</td>
                                                <td style={{ padding: '10px 12px', textAlign: 'right', color: '#6b7280' }}>{item.costTotal?.toLocaleString()}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Summary */}
                            <div style={{ maxWidth: 300, marginLeft: 'auto', marginTop: 16, padding: '12px 0' }}>
                                <SummaryRow label="ค่าสินค้า" value={`${totalSale.toLocaleString()} ฿`} />
                                {shipping > 0 && <SummaryRow label="ค่าจัดส่ง" value={`${shipping.toLocaleString()} ฿`} />}
                                {serviceFee > 0 && <SummaryRow label="ค่าบริการ" value={`${serviceFee.toLocaleString()} ฿`} />}
                                {discount > 0 && <SummaryRow label="ส่วนลด" value={`-${discount.toLocaleString()} ฿`} />}
                                <SummaryRow label="รวมสุทธิ" value={`${grandTotal.toLocaleString()} ฿`} bold />
                                <SummaryRow label="ต้นทุนรวม" value={`${totalCost.toLocaleString()} ฿`} />
                                <SummaryRow
                                    label="กำไร"
                                    value={`${profit.toLocaleString()} ฿`}
                                    bold
                                    color={profit >= 0 ? '#16a34a' : '#dc2626'}
                                />
                            </div>
                        </Card.Body>
                    </Card>

                    {/* Files */}
                    <Card style={cardStyle} className="mb-3">
                        <Card.Body>
                            <div style={sectionTitle}>ไฟล์ที่เกี่ยวข้อง</div>
                            {files.length === 0 ? (
                                <p style={{ color: '#9ca3af', fontSize: 13 }}>ยังไม่มีไฟล์</p>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                    {files.filter(x => !x.isAdmin).length > 0 && (
                                        <div>
                                            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>จากลูกค้า</div>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                                {files.filter(x => !x.isAdmin).map(f => (
                                                    <ImageViewer download key={f.fileUploadId} id={f.fileUploadId} alt="file" />
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {files.filter(x => x.isAdmin).length > 0 && (
                                        <div>
                                            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>จาก Admin</div>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                                {files.filter(x => x.isAdmin).map(f => (
                                                    <ImageViewer download key={f.fileUploadId} id={f.fileUploadId} alt="file" />
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Timeline Logs */}
                    <Card style={cardStyle} className="mb-3">
                        <Card.Body>
                            <div style={sectionTitle}>ประวัติรายการ</div>
                            {logs.length === 0 ? (
                                <p style={{ color: '#9ca3af', fontSize: 13 }}>ไม่มีประวัติ</p>
                            ) : (
                                <div style={{ position: 'relative', paddingLeft: 20 }}>
                                    <div style={{ position: 'absolute', left: 7, top: 8, bottom: 8, width: 2, background: '#e5e7eb' }} />
                                    {logs
                                        .sort((a, b) => new Date(b.createDate) - new Date(a.createDate))
                                        .map((log) => (
                                            <div key={log.id} style={{ position: 'relative', marginBottom: 16 }}>
                                                <div style={{
                                                    position: 'absolute', left: -20, top: 4,
                                                    width: 14, height: 14, borderRadius: '50%',
                                                    background: log.status?.color || '#9ca3af',
                                                    border: '2px solid #fff', boxShadow: '0 0 0 2px #e5e7eb',
                                                }} />
                                                <div style={{ background: '#f9fafb', borderRadius: 8, padding: '10px 14px' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                                                        <span style={{ fontWeight: 600, color: log.status?.color || '#374151', fontSize: 14 }}>
                                                            {log.status?.labelTh || 'ไม่ทราบสถานะ'}
                                                        </span>
                                                        <span style={{ fontSize: 11, color: '#9ca3af' }}>{DateTimeFormat(log.createDate)}</span>
                                                    </div>
                                                    <div style={{ fontSize: 12, color: '#6b7280' }}>{log.status?.detailTh}</div>
                                                    {log.description && (
                                                        <div style={{ marginTop: 6, fontSize: 13, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 10px' }}>
                                                            {log.description}
                                                        </div>
                                                    )}
                                                    <div style={{ marginTop: 4, fontSize: 11, color: '#9ca3af', textAlign: 'right' }}>
                                                        โดย: {log.isSystem ? 'ระบบ' : `${log.user?.firstName} ${log.user?.lastName}`}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Comments */}
                    {!IsEmptyGuid(data.entityUniqueIdentifierId) && (
                        <Card style={cardStyle} className="mb-3">
                            <Card.Body>
                                <CommentsBox euid={data.entityUniqueIdentifierId} />
                            </Card.Body>
                        </Card>
                    )}
                </Col>
            </Row>

            {/* Confirm Payment Modal */}
            <Modal show={showModal} onHide={handleClose} centered>
                <Modal.Header closeButton>
                    <Modal.Title>ยืนยันการชำระ</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                            {file?.id && <div style={{ marginBottom: 12 }}><ImageViewer id={file.id} /></div>}
                            <Form.Label>อัปโหลดรูปภาพ</Form.Label>
                            <ImageUpload
                                forceCrop={false}
                                onUploadComplete={(f) => setFile(f)}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>หมายเหตุ (ถ้ามี)</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name="description"
                                value={paymentDetails.description}
                                onChange={(e) => setPaymentDetails(p => ({ ...p, description: e.target.value }))}
                            />
                        </Form.Group>
                        <Button variant="primary" type="submit">ยืนยัน</Button>
                    </Form>
                </Modal.Body>
            </Modal>
        </React.Fragment>
    );
};

const cardStyle = {
    borderRadius: 12,
    border: 'none',
    boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
};

const avatarStyle = {
    width: 44, height: 44, borderRadius: '50%',
    background: 'linear-gradient(135deg, #1e3a5f, #2d6a9f)',
    color: '#fff', display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontWeight: 700, fontSize: 16, flexShrink: 0,
};

const sectionTitle = {
    fontSize: 13, fontWeight: 700, color: '#6b7280',
    textTransform: 'uppercase', letterSpacing: '0.05em',
    marginBottom: 12,
};

export default TransactionsDetails;
